import { useEffect, useRef, useState } from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { trackPromise } from "react-promise-tracker";
import { useAccordionContext } from "../contexts/AccordionContext";
import { useLockersContext } from "../contexts/LockersContext";
import { useSpecificLockerActionContext } from "../contexts/SpecificLockerActionContext";
import { Locker } from "../data/LockerDataTypes";
import { Api } from "../utils/Api";
import { SearchIcon } from "./Miscellaneous";
import { bottom } from "@popperjs/core/lib/enums";
import { SearchSettings } from "../data/SearchSettings";

const searchSettings: SearchSettings = {
    minLimit: 3,
    maxLimit: 100,
    notAllowedSpecials: ["\b", "\\b", "\f", "\\f", "\n",
        "\\n", "\r", "\\r", "\t", "\\t"]
}

const SearchInput = (): any => {
    const [searchValue, setSearchValue] = useState("");
    const [searchError, setSearchError] = useState("");
    const [alteredSearchValue, setAlteredSearchValue] = useState("");
    const { t } = useTranslation();
    const {
        searchFilter,
        setSearchFilter,
        lockerPluginOptions,
        setUnfilteredLockerResponse
    } = useLockersContext();
    const { setSelectedLocker } = useSpecificLockerActionContext();
    const { setAccordionExpanded } = useAccordionContext();

    const searchInputRef = useRef<HTMLInputElement>({} as any);
    const [showOnMouse, setShowOnMouse] = useState(false);
    const [showOnFocus, setShowOnFocus] = useState(false);

    useEffect(() => {
        if (!searchFilter) {
            resetSearchProperties();
            return;
        }

        setSelectedLocker({} as Locker)
        setShowOnMouse(false);
        performSearch();
        setAccordionExpanded(true);
    }, [searchFilter]);

    useEffect(() => {
        setAlteredSearchValue(getAlteredSearchValue());
    }, [searchValue]);

    useEffect(() => {
        if (searchError) {
            setSearchError(validateSearch());
        }
    }, [alteredSearchValue])

    const handleOnMouseEnter = () => {
        setShowOnMouse(true);
    }
    const handleOnMouseLeave = () => {
        setShowOnMouse(false);
    }
    const handleOnFocus = () => {
        setShowOnFocus(true);
    }
    const handleOnBlur = () => {
        setShowOnFocus(false);
    }
    const handleOnClickBlurForm = () => {
        searchInputRef.current.focus();
    }

    const resetSearchProperties = () => {
        setSearchValue("");
        setSearchError("");
        setSearchFilter("");
    }

    const getAlteredSearchValue = (): string => {
        return searchValue.trim();
    }

    const setSearch = (paramSearch: string): void => {
        if (paramSearch.length > searchSettings.maxLimit) {
            setSearchValue(prevSearchValue => {
                if (prevSearchValue) {
                    return prevSearchValue;
                } else {
                    paramSearch = searchValue.slice(0, searchSettings.maxLimit);
                    return paramSearch;
                }
            });
        }
        else {
            setSearchValue(paramSearch);
        }
    }

    const validateSearch = (): string => {
        let error = "";

        error = getInputLengthErrorMessage();
        if (error) {
            return error;
        }
        error = getNotAllowedSpecialErrorMessage();
        if (error) {
            return error;
        }

        return error;
    }

    const getInputLengthErrorMessage = (): string => {
        if ((alteredSearchValue.length < searchSettings.minLimit && alteredSearchValue.length >= 1)
            || alteredSearchValue.length > searchSettings.maxLimit) {
            const error = t('searchError1').replace("${minLimit}",
                searchSettings.minLimit.toString()).replace("${maxLimit}", searchSettings.maxLimit.toString());
            return error;
        }
        return "";
    }

    const getNotAllowedSpecialErrorMessage = (): string => {
        let notAllowedSpecial = searchSettings.notAllowedSpecials.find(nas => alteredSearchValue.includes(nas));
        if (notAllowedSpecial) {
            const error = t('searchError2').replace("${notAllowedSpecial}", notAllowedSpecial);
            return error;
        }
        return "";
    }

    const handleOnChange = (searchInput: string): void => {
        setSearch(searchInput);
    }

    const clearFocusFromSearchInput = (event: React.FormEvent<HTMLFormElement>) => {
        try {
            (event.currentTarget[0] as HTMLElement).blur();
        }
        catch {
            //if focus is still on search input, mobile/tablets keyboard will still be displayed
        }
    };

    const executeSearchAction = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const error = validateSearch();
        setSearchError(error);
        if (!error) {
            clearFocusFromSearchInput(event);
            setSearchFilter(searchValue);
        }
    };

    const performSearch = (): void => {
        trackPromise(Api.Get().SearchLockers(searchFilter, lockerPluginOptions.countryCode).then(r => {
            setUnfilteredLockerResponse(r)
        }));
    };


    const getTooltipMarkup = () => {
        return (
            <>
                <span>{t('searchBy')}</span>
                <ul>
                    <li>{t('easyboxName')}</li>
                    <li>{t('street')}</li>
                    <li>{t('city')}</li>
                    <li>{t('county')}</li>
                </ul>
            </>);
    };

    return (
        <form onSubmit={executeSearchAction} className='locker-plugin-search-input-form'>
            <OverlayTrigger show={showOnMouse || showOnFocus} placement={bottom} delay={100} overlay={
                <Tooltip className={searchError ? 'opacity-75 tooltip-error' : 'opacity-75'}
                    onMouseEnter={handleOnMouseEnter}
                    onMouseLeave={handleOnMouseLeave}
                    onFocus={handleOnFocus}
                    onBlur={handleOnBlur}>
                    <div className='locker-plugin-search-tooltip p-1'>
                        {searchError ? <span>{searchError}</span> : getTooltipMarkup()}
                    </div>
                </Tooltip>}>
                <input id="locker-search-input" className={searchError ? 'form-control locker-plugin-search-input input-error' : 'form-control locker-plugin-search-input'}
                    ref={searchInputRef}
                    type="text"
                    name="lockerSearch"
                    autoComplete="off"
                    placeholder={t("search")}
                    value={searchValue}
                    onChange={(e) => { handleOnChange(e.target.value) }}
                    onMouseEnter={handleOnMouseEnter}
                    onMouseLeave={handleOnMouseLeave}
                    onFocus={handleOnFocus}
                    onBlur={handleOnBlur} />
            </OverlayTrigger>

            {(
                searchValue.length != 0 &&
                <Button id="locker-clear-search" variant="light" size="sm" className="locker-plugin-clear-search-filter" onClick={resetSearchProperties}>X</Button>
            )}

            <Button id="locker-search-button" type="submit" variant={searchError ? "danger" : "primary"} size="sm" className="locker-plugin-perform-search-filter" onClick={handleOnClickBlurForm}><SearchIcon /></Button>
        </form>);
}

export default SearchInput;